import {
  type AttachmentCollectionFragment,
  type AttachmentVariantFragment,
  AttachmentVariantType,
  DeleteAttachmentDocument
} from "app/gql/graphqlSchema";
import {styled} from "styled-components";
import {IonCard, IonIcon, IonImg, useIonAlert, useIonViewWillLeave} from "@ionic/react";
import {useTranslation} from "react-i18next";
import documentPlaceholder from "app/employee/att/document-placeholder.png";
import type {ID, MaybeNil} from "app/utils/types";
import {isNil} from "app/utils/stdlib";
import {type CSSProperties, useCallback, useEffect, useRef, useState} from "react";
import {AttachmentCollectionView} from "app/employee/att/AttachmentCollectionView";
import {FstIonModal} from "app/employee/incdoc/FstIonModal";
import {AddAttachment} from "app/employee/att/AddAttachment";
import {FlexColumnCenter, FlexRight, FlexRow} from "app/employee/flexUtils";
import {trashOutline} from "ionicons/icons";
import {IonRowButton} from "app/employee/IonRowButton";
import {query} from "app/gql/client";
import {useLoadingContext} from "app/employee/LoadingContext";

const Container = styled.div`
  display: flex;
  flex-direction: row;
  overflow-x: auto;
`;

const Attachment = styled(IonCard)`
  min-width: 100px;
  max-width: 150px;
  cursor: pointer;
  display: flex;
  align-items: center;
  background-color: #f8f8f8;
  justify-content: stretch;
`;

const AttachmentThumbnail = styled(IonImg)`
  width: 150px;
`;

type MinimalVariant = Pick<AttachmentVariantFragment, "id" | "url" | "metadata" | "variantType">;

interface Props {
  attCollection: AttachmentCollectionFragment;
  name: MaybeNil<string>;
  onUploadCompleted?: () => void | Promise<void>;
  onDeleteCompleted?: () => void | Promise<void>;
  canUpload: boolean;
  canDelete: boolean;
  asDisabled?: boolean;
  accept?: string;
  hideHeader?: boolean;
}

const selectThumbnail = (variants: MinimalVariant[]) =>
  variants.find((v) => v.variantType === AttachmentVariantType.Thumbnail);

const getAspectRatio = (variant: MaybeNil<Pick<AttachmentVariantFragment, "metadata">>): CSSProperties => {
  const width = variant?.metadata?.find((m) => m.key === "width")?.value;
  const height = variant?.metadata?.find((m) => m.key === "height")?.value;
  if (!isNil(width) && !isNil(height)) {
    return {
      aspectRatio: `${width}/${height}`
    };
  } else {
    return {};
  }
};

export const AttachmentCollection = ({
  attCollection,
  name,
  onUploadCompleted,
  onDeleteCompleted,
  canUpload,
  canDelete,
  asDisabled,
  accept,
  hideHeader
}: Props) => {
  const {t} = useTranslation();
  const modal = useRef<HTMLIonModalElement>(null);
  const [viewAttachmentId, setViewAttachmentId] = useState<ID | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const prevLenRef = useRef(attCollection.attachments.length);
  const {startLoading, finishLoading, showExceptionError} = useLoadingContext();
  const [presentConfirmation] = useIonAlert();

  const viewFullscreen = (attachmentId: ID) => {
    setViewAttachmentId(attachmentId);
    modal.current?.present();
  };

  const closeModal = useCallback(() => {
    setViewAttachmentId(null);
    modal.current?.dismiss();
  }, []);

  useIonViewWillLeave(() => {
    closeModal();
  }, [closeModal]);

  // Scroll to the end of the list when new attachments are added
  useEffect(() => {
    const prevLen = prevLenRef.current;
    const currentLen = attCollection.attachments.length;
    if (currentLen > prevLen) {
      const el = containerRef.current;
      if (el) {
        // wait for the attachment to be added to the DOM
        requestAnimationFrame(() => {
          el.scrollTo({left: el.scrollWidth, behavior: "smooth"});
        });
      }
    }
    prevLenRef.current = currentLen;
  }, [attCollection.attachments.length]);

  const onDeleteAttachmentConfirmation = async (attachmentId: ID) => {
    await presentConfirmation({
      header: t("g.attachment.delete-confirmation.title"),
      buttons: [
        {
          text: t("g.cancel"),
          role: "cancel"
        },
        {
          text: t("g.delete"),
          role: "destructive",
          handler: async () => {
            await onDeleteAttachment(attachmentId);
          }
        }
      ]
    });
  };

  const onDeleteAttachment = async (attachmentId: ID) => {
    try {
      startLoading();
      await query(DeleteAttachmentDocument, {
        input: {
          collectionId: attCollection.id,
          attachmentId
        }
      });
      await onDeleteCompleted?.();
    } catch (e) {
      await showExceptionError(e);
    } finally {
      finishLoading();
    }
  };

  return (
    <>
      {!hideHeader && (
        <FlexRow>
          <div>{t("g.uploaded-files", {count: attCollection.attachments.length})}</div>
          {(canUpload || asDisabled) && (
            <FlexRight>
              <AddAttachment
                accept={accept}
                as={IonRowButton}
                attCollection={attCollection}
                disabled={!canUpload}
                onUploadCompleted={onUploadCompleted}
              />
            </FlexRight>
          )}
        </FlexRow>
      )}
      <Container ref={containerRef}>
        {attCollection.attachments.map((a) => {
          const thumbnail = selectThumbnail(a.variants);
          const url = thumbnail?.url ?? documentPlaceholder;
          const aspectRatio = getAspectRatio(thumbnail);
          return (
            <div key={a.id}>
              <Attachment key={a.id} onClick={() => viewFullscreen(a.id)}>
                <AttachmentThumbnail src={url} style={aspectRatio} />
              </Attachment>
              {(canDelete || asDisabled) && (
                <FlexColumnCenter>
                  <IonRowButton
                    disabled={!canDelete}
                    fill="outline"
                    onClick={() => onDeleteAttachmentConfirmation(a.id)}
                  >
                    <IonIcon icon={trashOutline} slot="start" />
                    {t("g.delete")}
                  </IonRowButton>
                </FlexColumnCenter>
              )}
            </div>
          );
        })}
        <FstIonModal ref={modal}>
          <AttachmentCollectionView att={attCollection} attId={viewAttachmentId} name={name} onClose={closeModal} />
        </FstIonModal>
      </Container>
    </>
  );
};
